import { useTranslation } from 'react-i18next'
import { Dialog } from 'primereact/dialog'
import Badge from '../../../components/ui/Badge'
import DetailList, { type DetailItem } from '../../../components/ui/DetailList'
import EmptyState from '../../../components/ui/EmptyState'
import LoadingSkeleton from '../../../components/ui/LoadingSkeleton'
import { formatDateTime } from '../../../lib/datetime'
import { htmlToDisplayText, looksLikeHtml } from '../../../lib/html'
import { statusTone } from './SentEmailColumns'
import { useSentEmailDetail } from '../hooks/useSentEmails'
import type { SentEmailDetail } from '../types'

interface Props {
  /** Görüntülenecek kaydın kimliği; `null` iken dialog kapalıdır ve **istek atılmaz**. */
  id: number | null
  onHide: () => void
}

/**
 * Karşılığı olmayan alanın yer tutucusu.
 *
 * <p>Anayasanın "boş bırakılır, `-` yazılmaz" kuralı açıkça **tablo hücreleri** içindir
 * (`constitution.md` → Page Standards → Boş hücre gösterimi) ve bu ekranın <b>liste</b> sütunlarında
 * aynen uygulanır. Detaydaki etiket/değer ızgarası farklı bir bağlamdır: orada boş bir değer, alanın
 * "değeri yok" olduğunu değil, verinin <b>yüklenmediğini/kırıldığını</b> düşündürür — etiket öksüz
 * kalır. Bu yüzden detayda açık bir yer tutucu kullanılır (kullanıcı kararı, 2026-08-06).
 */
const EMPTY = '-'

/** Boş/yalnız-boşluk değeri {@link EMPTY} ile değiştirir. */
function dash(value: string | null | undefined): string {
  return value && value.trim() !== '' ? value : EMPTY
}

/**
 * Admin → Bildirimler detayı (078) — **salt-okunur** modal. Kapsam 2026-08-07'de üç kanala açıldığı
 * için başlık "Bildirim Detayı"dır; Cc/Bcc gibi yalnız e-postada dolu olan alanlar web/PN
 * satırlarında `-` gösterir (alan gizlenmez — hangi bilgilerin taşınmadığı da bilgidir).
 *
 * <p>Footer'da yalnız **Kapat** vardır: kaydet/yeniden gönder/iptal butonu yoktur (spec FR-001).
 * Veri yalnız dialog açıkken çekilir (`useSentEmailDetail` `enabled` ile), böylece kapanışta bayat
 * içerik görünmez.
 *
 * <p><b>İçerik ve hata mesajı HTML olarak yorumlanMAZ</b> (spec FR-011): `<pre>` içinde **metin**
 * olarak basılır (`dangerouslySetInnerHTML` yok). Gövde e-posta şablonundan gelen HTML olabilir;
 * onu render etmek hem düzeni bozar hem de gereksiz bir XSS yüzeyi açardı.
 *
 * <p><b>2026-08-28:</b> içerik gövdesi artık ham HTML olarak da gösterilmiyor — etiketleri sökülüp
 * okunur metne çevriliyor ({@code htmlToDisplayText}). Hata mesajı ham kalır: o teknik bir iz, ne
 * taşıdığı aynen görünmelidir.
 */
export default function SentEmailDetailDialog({ id, onHide }: Readonly<Props>) {
  const { t } = useTranslation()
  const { data, isLoading, isError } = useSentEmailDetail(id)

  return (
    <Dialog
      visible={id != null}
      onHide={onHide}
      header={t('admin.sentEmails.detail.title')}
      modal
      dismissableMask
      style={{ width: '46rem', maxWidth: '95vw' }}
      className="admin-sent-email-dialog"
    >
      {isLoading && <LoadingSkeleton lines={6} />}
      {!isLoading && isError && (
        <EmptyState
          icon="pi pi-exclamation-triangle"
          title={t('admin.sentEmails.detail.loadError')}
        />
      )}
      {!isLoading && !isError && data && <DetailBody detail={data} />}
    </Dialog>
  )
}

function DetailBody({ detail }: Readonly<{ detail: SentEmailDetail }>) {
  const { t } = useTranslation()
  const label = (key: string) => t(`admin.sentEmails.detail.${key}`)

  const items: DetailItem[] = [
    { label: label('type'), value: dash(detail.notificationTypeName) },
    // İşlem Kodu: bildirimi hangi olayın doğurduğu. Kolon 078'den SONRA eklendiği için eski
    // satırlarda boştur → "-" görünür (ham kimlik sunucudan hiç gelmez).
    { label: label('operationCode'), value: dash(detail.operationCodeName) },
    {
      label: label('status'),
      // Renk eşlemesi listeyle TEK KAYNAKTAN gelir: iki yerde ayrı `switch` tutmak, ör. "İptal
      // Edildi"nin listede kırmızı, detayda mavi görünmesine yol açardı.
      value: detail.sendStatusName ? (
        <Badge tone={statusTone(detail.sendStatusId)}>{detail.sendStatusName}</Badge>
      ) : (
        EMPTY
      ),
    },
    { label: label('recipient'), value: dash(detail.contactInformation) },
    // Bildirimin FİİLEN gittiği sicil (100). "Alıcı"nın hemen altında durur: çoklu sicilli
    // personelde ikisi farklıdır ve detayın amacı tam olarak "gerçekte ne gönderildi"dir.
    // Yalnız push satırlarında dolar; diğerlerinde `dash` boş göstergesini yazar.
    { label: label('sentRecipient'), value: dash(detail.sendContactInformation) },
    { label: label('cc'), value: dash(detail.mailCc) },
    { label: label('bcc'), value: dash(detail.mailBcc) },
    {
      label: label('planned'),
      value: detail.plannedSendingTime ? formatDateTime(detail.plannedSendingTime) : EMPTY,
    },
    {
      label: label('sending'),
      value: detail.sendingTime ? formatDateTime(detail.sendingTime) : EMPTY,
    },
    { label: label('retryCount'), value: String(detail.retryCount) },
    { label: label('templateId'), value: dash(detail.templateId) },
    { label: label('employeeId'), value: dash(detail.employeeId) },
  ]

  return (
    <div className="admin-sent-email-detail">
      <DetailList items={items} />

      {/*
        Hata bloğu yer tutucu ALMAZ ve hatasız kayıtta hiç basılmaz — bilinçli istisna. Bu bir
        etiket/değer alanı değil, kırmızı zeminli bir UYARI; "-" yazan kırmızı bir kutu, sorunsuz bir
        gönderimde sorun varmış izlenimi verirdi. Alanın yokluğu burada "hata yok" demektir.
      */}
      {detail.errorMessage && (
        <section className="admin-sent-email-error">
          <h4>{label('errorMessage')}</h4>
          <pre>{detail.errorMessage}</pre>
        </section>
      )}

      {/*
        İlgili Kayıtlar: ham kimlik ASLA gösterilmez (spec US3.3) — bağ yoksa ya da hedef kayıt
        silinmişse sunucu `null` döndürür ve karşısına "-" yazılır. Bölüm her zaman görünür: satırı
        gizlemek, "bu bildirimin toplantı bağı yok" ile "böyle bir alan yok"u ayırt edilemez kılardı.
      */}
      <section className="admin-sent-email-refs">
        <h4>{label('references')}</h4>
        <DetailList
          items={[
            { label: label('meeting'), value: dash(detail.meetingName) },
            { label: label('minutes'), value: dash(detail.minutesName) },
          ]}
        />
      </section>

      {/* Konu + içerik TR ve EN birlikte: "gerçekte ne gönderildi" sorusu iki dili de kapsar. */}
      <ContentBlock
        heading={label('trContent')}
        subject={detail.subjectTr}
        subjectLabel={label('subject')}
        content={detail.contentTr}
      />
      <ContentBlock
        heading={label('enContent')}
        subject={detail.subjectEn}
        subjectLabel={label('subject')}
        content={detail.contentEn}
      />
    </div>
  )
}

/**
 * Bir dilin konu + gövdesi. Gövde `<pre>` içinde **metin**tir — HTML yorumlanmaz (spec FR-011).
 *
 * <p>Gövde ham HTML olarak DEĞİL, {@link htmlToDisplayText} ile **etiketleri sökülmüş** hâlde basılır
 * (kullanıcı isteği 2026-08-28: <i>"html değil de normal halde görünsün"</i>). Kayıtların üçte biri
 * Quill'den gelen HTML taşıyor ve okuyan kişi `<p>…</p><p><br></p>` gürültüsüyle karşılaşıyordu.
 * Dönüşüm satır yapısını korur; `<pre>` zaten `white-space: pre-wrap` + `--sans` olduğu için paragraf
 * araları ekranda doğru görünür.
 *
 * <p><b>FR-011 ihlal edilmez:</b> dönen değer bir metindir ve metin düğümü olarak basılır —
 * `dangerouslySetInnerHTML` yoktur, XSS yüzeyi açılmaz. Değişen tek şey, etiketlerin kendisi yerine
 * aralarındaki metnin görünmesi.
 */
function ContentBlock({
  heading,
  subject,
  subjectLabel,
  content,
}: Readonly<{
  heading: string
  subject: string | null
  subjectLabel: string
  content: string | null
}>) {
  return (
    <section className="admin-sent-email-content">
      <h4>{heading}</h4>
      <DetailList items={[{ label: subjectLabel, value: dash(subject) }]} />
      {/* O dilde gövde yoksa boş bir çerçeve değil, açık yer tutucu gösterilir. */}
      {/*
        `looksLikeHtml` kapısı ZORUNLU: ekran üç kanalı birden gösteriyor (e-posta HTML, web/PN
        düz metin) ve dönüşümü koşulsuz uygulamak düz metni sessizce kırpardı — `<yer tutucu>`
        etiket sanılıp silinir, `5 < 3 > 1` → `5 1` olurdu. Aynı tuzak `lib/html.ts`'te
        `hasVisibleText` ↔ `hasContent` ayrımıyla zaten bir kez öğrenilmiş. Push kapısı code
        review'ünde yakalandı (bugün canlıda düz metin satırlarında `<` yok — sessiz kalırdı).
      */}
      <pre>{dash(looksLikeHtml(content) ? htmlToDisplayText(content) : content)}</pre>
    </section>
  )
}
